/**
 * Marking a section with what it discloses under the ESRS.
 *
 * A sustainability statement is not read from front to back. It is read by
 * somebody looking for one disclosure: an auditor checking `E1-6`, a regulator
 * checking that `S1` was addressed at all, a machine building the digital
 * taxonomy. None of them can find anything in a document whose sections only
 * have titles, because a chapter called "Notre empreinte carbone" says nothing
 * about which requirement it answers.
 *
 * So any section can carry a **reference** and a **materiality state**. That is
 * deliberately not a section kind: what a section discloses is orthogonal to
 * what it is. A table, a chart, a note and a whole chapter can each answer
 * `E1-6`, and a fifteenth kind called "ESRS block" would only mean the tag was
 * unavailable on the other fourteen.
 *
 * Three things become cheap once this exists, and none of them is possible
 * without it: the disclosure index builds itself the way the contents page
 * does, a coverage check can say "E1 is declared material and nothing in the
 * document answers E1-6", and the datapoint tagging the digital taxonomy needs
 * has somewhere to hang.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **What this file will and will not assert.**
 *
 * The eleven standards are a short, settled list and are named here. The
 * disclosure requirements inside them are not: there are roughly a hundred,
 * they are revised, and a list of them written from memory would be wrong in
 * ways nobody would notice until an auditor did. So a reference is **validated
 * by shape rather than checked against a list**. `E1-6` is accepted because it
 * is well formed and belongs to a standard that exists, not because this file
 * claims to know that E1 has a sixth requirement.
 *
 * That is the honest line. A reference this cannot vouch for is still useful:
 * the standard it belongs to is derived from it, which is all the index and
 * the coverage check actually need.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Pure. Codes and names, no rendering.
 */

/** The four parts a sustainability statement is organised into. */
export const ESRS_PILLARS = ['general', 'environment', 'social', 'governance'] as const

export type EsrsPillar = (typeof ESRS_PILLARS)[number]

/**
 * The standards, in the order a statement presents them.
 *
 * The order matters: it is the order of the document, so an index built from
 * this is in the order somebody expects to read it rather than in the order
 * the sections happen to have been written.
 */
export const ESRS_STANDARDS = {
  ESRS2: { code: 'ESRS 2', pillar: 'general' },
  E1: { code: 'E1', pillar: 'environment' },
  E2: { code: 'E2', pillar: 'environment' },
  E3: { code: 'E3', pillar: 'environment' },
  E4: { code: 'E4', pillar: 'environment' },
  E5: { code: 'E5', pillar: 'environment' },
  S1: { code: 'S1', pillar: 'social' },
  S2: { code: 'S2', pillar: 'social' },
  S3: { code: 'S3', pillar: 'social' },
  S4: { code: 'S4', pillar: 'social' },
  G1: { code: 'G1', pillar: 'governance' },
} as const satisfies Record<string, { code: string; pillar: EsrsPillar }>

export type EsrsStandard = keyof typeof ESRS_STANDARDS

export const ESRS_STANDARD_IDS = Object.keys(ESRS_STANDARDS) as readonly EsrsStandard[]

/**
 * The families of requirement that belong to ESRS 2 rather than to a topic.
 *
 * These are written as `GOV-1` and `SBM-3` without a standard in front of
 * them, so a reference has to be recognised by its prefix instead. Kept as a
 * list because it is short and settled, unlike the numbering inside each.
 */
const GENERAL_PREFIXES = ['BP', 'GOV', 'SBM', 'IRO', 'MDR'] as const

/**
 * How much of a requirement the report says about itself.
 *
 * All four are real answers. "Not material" in particular is a disclosure in
 * its own right: a statement that simply omits a topic is not the same as one
 * that says the topic was assessed and found not to be material, and only the
 * second is compliant.
 */
export const MATERIALITIES = ['material', 'notMaterial', 'phasedIn', 'byReference'] as const

export type Materiality = (typeof MATERIALITIES)[number]

export const DEFAULT_MATERIALITY: Materiality = 'material'

/** What a section says it discloses. */
export interface SectionTag {
  /** A disclosure reference, normalised: `E1-6`, `GOV-1`. Never blank. */
  readonly ref: string
  readonly materiality: Materiality
}

/** A reference broken into the parts anything downstream needs. */
export interface EsrsRef {
  readonly standard: EsrsStandard
  readonly pillar: EsrsPillar
  /** The reference as it should be written. */
  readonly ref: string
}

/**
 * A reference read out of what somebody typed.
 *
 * Forgiving about the things people actually type and strict about the shape:
 * lower case, an `ESRS ` in front, a space instead of the hyphen, and stray
 * spaces at the ends are all accepted, because refusing them would be
 * pedantry aimed at somebody who typed the right reference.
 *
 * Null when it is not a reference at all. A section with an unreadable tag is
 * better untagged than tagged wrongly: the index would send a reader to the
 * wrong page, which is worse than sending them nowhere.
 */
export function parseEsrsRef(text: string): EsrsRef | null {
  const cleaned = text
    .trim()
    .toUpperCase()
    .replace(/^ESRS\s*/, '')
    .replace(/\s+/g, ' ')

  if (cleaned === '') return null

  // A topical requirement: E1-6, S1-14, G1-3. Also the bare standard, E1,
  // which is what a chapter introducing a topic answers.
  const topical = /^([ESG])\s?(\d)(?:\s?-\s?(\d{1,2}))?$/.exec(cleaned)
  if (topical) {
    const [, letter = '', index = '', number] = topical
    const id = `${letter}${index}`
    if (!isStandard(id)) return null
    return {
      standard: id,
      pillar: ESRS_STANDARDS[id].pillar,
      ref: number === undefined ? id : `${id}-${Number(number)}`,
    }
  }

  // A general requirement: GOV-1, SBM-3, IRO-2, BP-2, MDR-P.
  const general = /^([A-Z]{2,3})\s?-\s?([A-Z0-9]{1,2})$/.exec(cleaned)
  if (general) {
    const [, family = '', suffix = ''] = general
    if (!(GENERAL_PREFIXES as readonly string[]).includes(family)) return null
    return { standard: 'ESRS2', pillar: 'general', ref: `${family}-${suffix}` }
  }

  // ESRS 2 itself, for the chapter that introduces the general disclosures.
  if (cleaned === '2') return { standard: 'ESRS2', pillar: 'general', ref: 'ESRS 2' }

  return null
}

function isStandard(value: string): value is EsrsStandard {
  return value in ESRS_STANDARDS
}

/** Whether what somebody has typed so far is a usable reference. */
export function isEsrsRef(text: string): boolean {
  return parseEsrsRef(text) !== null
}

/** A stored materiality, if it is one this version offers. */
export function readMateriality(value: unknown): Materiality {
  return typeof value === 'string' && (MATERIALITIES as readonly string[]).includes(value)
    ? (value as Materiality)
    : DEFAULT_MATERIALITY
}

/**
 * A tag read off a stored section, or nothing.
 *
 * A tag whose reference no longer parses is dropped rather than kept as
 * written. The alternative is an index with an entry nobody can act on, and a
 * coverage check quietly counting it towards a standard it may not belong to.
 */
export function readTag(ref: unknown, materiality: unknown): SectionTag | null {
  if (typeof ref !== 'string') return null

  const parsed = parseEsrsRef(ref)
  if (!parsed) return null

  return { ref: parsed.ref, materiality: readMateriality(materiality) }
}

/**
 * How the standards sort.
 *
 * By the order they are declared in, which is the order of the statement. A
 * lookup rather than a comparison so it is one map access per entry instead of
 * a scan of the list.
 */
const STANDARD_ORDER = new Map(ESRS_STANDARD_IDS.map((id, at) => [id, at]))

export function standardOrder(standard: EsrsStandard): number {
  return STANDARD_ORDER.get(standard) ?? ESRS_STANDARD_IDS.length
}

/**
 * How references sort inside a standard.
 *
 * By family first and then by number, numerically, so `E1-10` comes after
 * `E1-9` rather than between `E1-1` and `E1-2` the way a plain string sort
 * would put it. That ordering is the whole reason this is not `localeCompare`.
 */
export function compareRefs(a: string, b: string): number {
  const left = splitRef(a)
  const right = splitRef(b)

  if (left.family !== right.family) return left.family < right.family ? -1 : 1
  if (left.number !== right.number) return left.number - right.number
  return left.suffix < right.suffix ? -1 : left.suffix > right.suffix ? 1 : 0
}

function splitRef(ref: string): { family: string; number: number; suffix: string } {
  const at = ref.indexOf('-')
  if (at === -1) return { family: ref, number: 0, suffix: '' }

  const family = ref.slice(0, at)
  const rest = ref.slice(at + 1)
  const number = Number(rest)

  // `MDR-P` has a letter where a number would be, so it sorts by the letter
  // and shares a number of zero with the bare standard.
  return Number.isFinite(number)
    ? { family, number, suffix: '' }
    : { family, number: 0, suffix: rest }
}
